<template>
  <div class="pb-10">
    <Head title="Nueva Cotización" />

    <!-- Encabezado -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Nueva Cotización</h1>
      <p class="text-gray-500">Crea una nueva cotización para tus clientes</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Información del Cliente -->
      <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 class="text-xl font-bold text-gray-900 mb-1">Información del Cliente</h2>
        <p class="text-sm text-gray-500 mb-6">Datos del cliente para la cotización</p>
        
        <FindOrCreateClient class="mb-4" @addClient="takeClient" />
        
        <div v-if="form.client.name" class="space-y-4">
          <text-input v-model="form.client.name" label="Nombre del Cliente *" placeholder="Nombre o empresa del cliente" />
          <text-input v-model="form.client.email" label="Email" placeholder="email@cliente.com" />
          <text-input v-model="form.client.phone" label="Teléfono" placeholder="+1 234 567 8900" />
          <text-area-input v-model="form.client.address" label="Dirección" placeholder="Dirección completa del cliente" />
          <text-input v-model="form.comentary_project" label="Nombre del Proyecto" placeholder="Nombre descriptivo del proyecto" />
        </div>
      </div>

      <!-- Detalles de la Cotización -->
      <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 class="text-xl font-bold text-gray-900 mb-1">Detalles de la Cotización</h2>
        <p class="text-sm text-gray-500 mb-6">Configuración y fechas de la cotización</p>

        <div class="space-y-4">
          <text-input v-model="quoteNumber" label="Número de Cotización (Generado Auto.)" readonly placeholder="Calculado al guardar" class="bg-gray-50 text-gray-500" />
          <text-input v-model="form.date" label="Fecha de Cotización" type="date" />
          <text-input v-model="form.valid_until" label="Válida Hasta" type="date" />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">Estado</label>
            <select disabled class="form-select w-full rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500 bg-gray-50">
              <option selected>Borrador</option>
            </select>
          </div>
          <label class="flex items-center gap-2 mt-4 cursor-pointer">
            <input v-model="applyTaxes" type="checkbox" class="form-checkbox text-orange-600 rounded" />
            <span class="text-sm">Aplicar impuestos</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Items de la Cotización -->
    <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm mb-6">
      <div class="flex justify-between items-start mb-6">
        <div>
          <h2 class="text-xl font-bold text-gray-900 mb-1">Items de la Cotización</h2>
          <p class="text-sm text-gray-500">Productos o servicios incluidos en la cotización</p>
        </div>
        <button class="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium" @click="addQuickProduct">
          <i class="fa fa-plus" /> Agregar Item
        </button>
      </div>

      <div class="flex items-center gap-3 mb-6">
        <span class="text-sm font-medium text-gray-700">Usar servicio:</span>
        <select-input v-model="selectedProductId" class="w-64" :options="productOptions" placeholder="Seleccionar servicio..." @change="onProductSelect" />
        <button class="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm" @click="addSelectedProduct">
          <i class="fa fa-bookmark" /> Agregar
        </button>
      </div>

      <div class="space-y-4">
        <div v-for="(item, index) in form.products" :key="index" class="border border-gray-200 rounded-lg p-5">
          <div class="flex justify-between items-center mb-4">
            <h3 class="font-bold text-gray-800">Item {{ index + 1 }}</h3>
            <button class="text-gray-400 hover:text-red-500 transition-colors" @click="removeProduct(index)">
              <i class="fa fa-trash" />
            </button>
          </div>
           
          <div class="space-y-4">
            <text-input v-model="item.name" label="Título del Item" placeholder="Nombre del producto o servicio..." />
             
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
              <div class="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500">
                <div class="bg-gray-50 border-b border-gray-200 px-3 py-2 flex gap-2">
                  <button type="button" class="p-1 hover:bg-gray-200 rounded"><i class="fa fa-bold text-gray-600" /></button>
                  <button type="button" class="p-1 hover:bg-gray-200 rounded"><i class="fa fa-italic text-gray-600" /></button>
                  <button type="button" class="p-1 hover:bg-gray-200 rounded"><i class="fa fa-underline text-gray-600" /></button>
                  <div class="w-px h-5 bg-gray-300 mx-1 self-center" />
                  <button type="button" class="p-1 hover:bg-gray-200 rounded"><i class="fa fa-list-ul text-gray-600" /></button>
                  <button type="button" class="p-1 hover:bg-gray-200 rounded"><i class="fa fa-list-ol text-gray-600" /></button>
                </div>
                <textarea v-model="item.description" rows="3" class="w-full border-none focus:ring-0 text-sm p-3" placeholder="Descripción detallada del producto o servicio..." />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <text-input v-model.number="item.quantity" type="number" label="Cantidad" />
              <text-input v-model.number="item.price" type="number" step="0.01" label="Precio Unitario" />
              <text-input :value="(Number(item.quantity || 0) * Number(item.price || 0)).toFixed(2)" readonly label="Total" class="bg-gray-50 bg-opacity-50 text-gray-600" />
            </div>
          </div>
        </div>

        <div v-if="form.products.length === 0" class="text-center py-8 text-gray-400 italic border border-dashed border-gray-300 rounded-lg">
          No hay items agregados a la cotización.
        </div>
      </div>

      <div class="mt-8 flex flex-col items-end border-t border-gray-100 pt-6">
        <div class="w-64 space-y-3 shrink-0">
          <div class="flex justify-between items-center w-full">
            <span class="mr-4">Tipo Descuento:</span>
            <select v-model="form.discount.type" class="w-32 form-select rounded-lg border-gray-300 py-1 text-sm">
              <option value="percentage">%</option>
              <option value="amount">$ Fijo</option>
            </select>
          </div>
          <div class="flex justify-between items-center w-full">
            <span class="mr-4">Valor:</span>
            <input v-model="form.discount.value" type="number" min="0" class="w-32 form-input rounded-lg border-gray-300 py-1 text-sm text-right" />
          </div>
          <hr class="my-2 border-gray-200" />
          <div class="flex justify-between text-gray-600">
            <span>Subtotal:</span>
            <span>${{ getSubtotal() }}</span>
          </div>
          <div v-if="getDiscountAmount() > 0" class="flex justify-between text-gray-600">
            <span>Descuento:</span>
            <span class="text-red-500">-${{ getDiscountAmount() }}</span>
          </div>
          <div class="flex justify-between font-bold text-lg pt-3 border-t border-gray-200">
            <span>Total:</span>
            <span>${{ getTotal() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Notas y Términos -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 class="text-lg font-bold text-gray-900 mb-1">Notas</h2>
        <p class="text-sm text-gray-500 mb-4">Notas adicionales para el cliente</p>
        <textarea v-model="form.notes" rows="4" class="w-full form-textarea rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500" placeholder="Notas adicionales, instrucciones especiales, etc." />
      </div>
      <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 class="text-lg font-bold text-gray-900 mb-1">Términos y Condiciones</h2>
        <p class="text-sm text-gray-500 mb-4">Términos y condiciones de la cotización</p>
        <textarea v-model="form.terms" rows="4" class="w-full form-textarea rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500" placeholder="Términos de pago, garantías, condiciones de entrega, etc." />
      </div>
    </div>

    <!-- Etiquetas (Placeholder for Additionals si se implementan más campos) -->
    <div class="bg-white border border-gray-100 rounded-xl p-6 shadow-sm mb-6">
      <h2 class="text-lg font-bold text-gray-900 mb-1">Etiquetas de Seguimiento</h2>
      <p class="text-sm text-gray-500 mb-4">Organiza y categoriza tus cotizaciones con etiquetas</p>
      <button class="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm">
        <i class="fa fa-tags" /> Etiquetas
      </button>
    </div>

    <!-- Botones de Acción -->
    <div class="flex justify-between items-center bg-white border border-gray-100 rounded-xl p-4 shadow-sm sticky bottom-4 z-10">
      <button class="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors" @click="showPreview = true">
        <i class="fa fa-eye" /> Vista Previa
      </button>
      <div class="flex gap-3">
        <button class="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors" @click="goBack">
          Cancelar
        </button>
        <button :disabled="form.processing" class="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 flex items-center gap-2 transition-colors disabled:opacity-50" @click="saveQuote">
          <i class="fa fa-save" /> Guardar Cotización
        </button>
      </div>
    </div>

    <!-- Modal Vista Previa -->
    <div v-show="showPreview" class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 sm:p-6 transition-opacity">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden" @click.stop>
        <!-- Sticky Header with Close Button -->
        <div class="bg-white border-b border-gray-100 p-4 flex justify-between items-center sticky top-0 z-20 shadow-sm shrink-0">
          <h3 class="font-bold text-gray-800 text-lg">Vista Previa de Cotización</h3>
          <button class="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" @click="showPreview = false">
            <i class="fa fa-times" /> Cerrar Vista Previa
          </button>
        </div>

        <div id="preview-document" class="flex-1 overflow-y-auto min-h-0 p-8 sm:p-12 relative">
          <div class="flex justify-between items-start mb-12">
            <div>
              <img v-if="account?.logo" :src="account.logo" alt="Logo" class="max-h-16 mb-4 object-contain" />
              <div v-else class="text-3xl font-black tracking-tighter mb-4 text-gray-800 flex items-center gap-2">
                <i class="fa fa-building text-4xl text-gray-400" />
              </div>
               
              <h3 class="font-bold text-lg text-gray-900">{{ account?.name || 'Nombre de la Empresa' }}</h3>
              <p v-if="account?.phone || account?.email" class="text-sm text-gray-600">
                <span v-if="account?.phone">Tel: {{ account.phone }}</span>
                <span v-if="account?.phone && account?.email"> &nbsp; </span>
                <span v-if="account?.email">Email: {{ account.email }}</span>
              </p>
              <p v-if="account?.address" class="text-sm text-gray-600">{{ account.address }}</p>
            </div>
            <div class="text-right">
              <h1 class="text-3xl font-bold text-gray-900 mb-2 uppercase tracking-wide">COTIZACIÓN</h1>
              <p class="text-gray-600 font-medium tracking-wider">{{ quoteNumber || 'Borrador' }}</p>
              <span class="inline-block mt-2 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">Borrador</span>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-12 mb-10">
            <div>
              <h4 class="font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">CLIENTE</h4>
              <p class="font-medium text-gray-800">{{ form.client.name || 'Nombre del Cliente' }}</p>
              <p v-if="form.client.email" class="text-sm text-gray-600">{{ form.client.email }}</p>
              <p v-if="form.client.phone" class="text-sm text-gray-600">{{ form.client.phone }}</p>
              <p v-if="form.client.address" class="text-sm text-gray-600 mt-1">{{ form.client.address }}</p>
            </div>
            <div>
              <h4 class="font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">DETALLES</h4>
              <div class="grid grid-cols-2 gap-y-2 text-sm">
                <span class="text-gray-600">Fecha:</span>
                <span class="font-medium text-gray-900">{{ formatDate(form.date) || '-' }}</span>
                 
                <span class="text-gray-600">Validez:</span>
                <span class="font-medium text-gray-900">{{ formatDate(form.valid_until) || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="mb-10">
            <h4 class="font-bold text-gray-900 mb-4">ITEMS</h4>
            <div class="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 p-1">
              <table class="w-full text-left text-sm">
                <thead class="bg-gray-100/50">
                  <tr>
                    <th class="py-3 px-4 font-semibold text-gray-900 rounded-tl-lg">Descripción</th>
                    <th class="py-3 px-4 font-semibold text-gray-900 text-center">Cant.</th>
                    <th class="py-3 px-4 font-semibold text-gray-900 text-right">Precio Unit.</th>
                    <th class="py-3 px-4 font-semibold text-gray-900 text-right rounded-tr-lg">Total</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white">
                  <tr v-for="(item, idx) in form.products" :key="idx">
                    <td class="py-4 px-4 align-top w-1/2">
                      <div class="font-medium text-gray-900 mb-1">{{ item.name || 'Sin título' }}</div>
                      <div class="text-gray-500 whitespace-pre-wrap">{{ item.description }}</div>
                    </td>
                    <td class="py-4 px-4 text-center align-top">{{ item.quantity }}</td>
                    <td class="py-4 px-4 text-right align-top">${{ Number(item.price).toFixed(2) }}</td>
                    <td class="py-4 px-4 text-right align-top font-medium text-gray-900">${{ (item.quantity * item.price).toFixed(2) }}</td>
                  </tr>
                  <tr v-if="form.products.length === 0">
                    <td colspan="4" class="py-6 px-4 text-center text-gray-400">Sin items</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Totales Preview -->
          <div class="flex justify-end mb-12">
            <div class="w-72">
              <div class="flex justify-between py-2 text-gray-600">
                <span>Subtotal:</span>
                <span>${{ getSubtotal() }}</span>
              </div>
              <div v-if="getDiscountAmount() > 0" class="flex justify-between py-2 text-gray-600 border-b border-gray-100">
                <span>Descuento:</span>
                <span class="text-red-500">-${{ getDiscountAmount() }}</span>
              </div>
              <div class="flex justify-between py-3 font-bold text-xl text-gray-900 bg-gray-100 px-4 mt-2 rounded-lg">
                <span>TOTAL:</span>
                <span>${{ getTotal() }}</span>
              </div>
            </div>
          </div>

          <!-- Pie de página Preview -->
          <div class="border-t border-gray-200 pt-6 mt-12 pb-6">
            <p v-if="form.notes" class="text-sm text-gray-600 font-medium mb-1">Notas:</p>
            <p v-if="form.notes" class="text-sm text-gray-500 mb-6 whitespace-pre-wrap">{{ form.notes }}</p>
             
            <p v-if="form.terms" class="text-sm text-gray-600 font-medium mb-1">Términos y condiciones:</p>
            <p v-if="form.terms" class="text-sm text-gray-500 mb-6 whitespace-pre-wrap">{{ form.terms }}</p>
             
            <p class="text-sm text-gray-400 text-center mt-6">Gracias por considerar nuestros servicios. Esta cotización es válida por el tiempo especificado.</p>
          </div>
        </div>
        
        <!-- Controles Modal -->
        <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3 rounded-b-2xl shrink-0">
          <button class="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors" @click="showPreview = false">
            Cerrar
          </button>
          <button class="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2" @click="downloadPdf">
            <i class="fa fa-download" /> Descargar PDF
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue'
import { Head, Link } from '@inertiajs/inertia-vue3'
import TextInput from '@/Shared/TextInput.vue'
import TextAreaInput from '@/Shared/TextAreaInput.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import FindOrCreateClient from '@/Shared/FindOrCreateClient.vue'

export default {
  components: {
    Layout, Head, Link, TextInput, TextAreaInput, SelectInput, FindOrCreateClient,
  },
  layout: Layout,
  props: {
    products: Object,
    auth: Object,
    account: Object,
    client: Object,
  },
  data() {
    return {
      showPreview: false,
      applyTaxes: false,
      selectedProductId: '',
      quoteNumber: 'Borrador (Auto)',
      form: this.$inertia.form({
        client: {
          id: null,
          name: '',
          email: '',
          phone: '',
          address: '',
          identification: '0000',
        },
        date: new Date().toISOString().substr(0, 10),
        valid_until: '',
        comentary_project: '',
        notes: '',
        terms: '',
        products: [],
        comentary: '', 
        isCredit: false,
        estimate: true,
        discount: {
          type: 'percentage',
          value: 0,
        },
        change: 1,
      }),
    }
  },
  computed: {
    productOptions() {
      if(!this.products || !this.products.data) return []
      return this.products.data.map(p => ({
        id: p.id,
        name: `${p.code ? p.code+' - ' : ''}${p.name} ($${p.prices?.[0]?.price || 0})`,
      }))
    },
  },
  mounted() {
    if (this.client) {
      this.takeClient(this.client)
    }

    // Deshabilitar scroll cuando modal está abierto
    this.$watch('showPreview', (val) => {
      if (val) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    })
  },
  methods: {
    formatDate(dateStr) {
      if(!dateStr) return ''
      const [y, m, d] = dateStr.split('-')
      if(!y || !m || !d) return dateStr
      return `${d}/${m}/${y}`
    },
    takeClient(client) {
      this.form.client = { ...this.form.client, ...client }
      if(!this.form.client.identification) {
        this.form.client.identification = '0000'
      }
    },
    onProductSelect(val) {
      this.selectedProductId = val
    },
    addSelectedProduct() {
      if (!this.selectedProductId) return
      const product = this.products.data.find(p => p.id == this.selectedProductId)
      if (product) {
        this.form.products.push({
          id: product.id,
          name: product.name,
          description: product.description || '',
          price: product.prices?.[0]?.price || 0,
          quantity: 1,
          quick: false,
          fee: 0,
        })
        this.selectedProductId = ''
      }
    },
    addQuickProduct() {
      this.form.products.push({
        id: 'quick',
        name: '',
        description: '',
        price: 0,
        quantity: 1,
        quick: true,
        fee: 0,
      })
    },
    removeProduct(index) {
      this.form.products.splice(index, 1)
    },
    getSubtotal() {
      const sub = this.form.products.reduce((acc, p) => acc + (parseFloat(p.price||0) * parseInt(p.quantity||0)), 0)
      return sub.toFixed(2)
    },
    getDiscountAmount() {
      const sub = parseFloat(this.getSubtotal())
      if (this.form.discount.type === 'percentage') {
        return (sub * (parseFloat(this.form.discount.value||0) / 100)).toFixed(2)
      }
      return parseFloat(this.form.discount.value || 0).toFixed(2)
    },
    getTotal() {
      const sub = parseFloat(this.getSubtotal())
      const disc = parseFloat(this.getDiscountAmount())
      return Math.max(0, sub - disc).toFixed(2)
    },
    goBack() {
      window.history.back()
    },
    downloadPdf() {
      // Para futura implementación
      alert('La descarga del borrador en PDF estará disponible luego de guardar o en su pantalla final.')
    },
    saveQuote() {
      // Validate
      if (!this.form.client.name) {
        alert('El nombre del cliente es obligatorio')
        return
      }
      if (this.form.products.length === 0) {
        alert('Debes agregar al menos un item a la cotización.')
        return
      }

      // Format comentary to include project, notes and terms
      let fullComment = ''
      if (this.form.comentary_project) fullComment += `Proyecto: ${this.form.comentary_project}\n\n`
      if (this.form.notes) fullComment += `Notas:\n${this.form.notes}\n\n`
      if (this.form.terms) fullComment += `Términos:\n${this.form.terms}\n\n`
      if (fullComment === '') fullComment = 'Cotización'
      
      this.form.comentary = fullComment
      this.form.post('/quotes')
    },
  },
}
</script>
